import React, { useEffect, useState } from "react";
import { Box, Typography } from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import { EditableColumnDef, EditableTable } from "../../../../../../shared/components/table/inline-edit-table";
import { CustomSelect, CustomTextField } from "../../../../../../shared/components/inputfields/form-elements";
import { StyledButton } from "../../../../../../shared/components/buttons/custom-button";
import { FacilityInformation, FacilityFarmCapacityInformation } from "../../../sc-models/facility-product-dto";
import { dtrackItServiceV4 } from "../../../sc-service/dtrackit.service";
import { useFormActions } from "../../../../../../shared/hooks/form-builder.hooks";
import { Utills } from "../../../../../../shared/app-configs/utilit";
import { useApi } from "../../../../../../shared/hooks/useApi";
import { API_ROUTES } from "../../../../../../shared/app-configs/sc-app-config";
import { useParams } from "react-router-dom";
import { useTransactionCertificateStore } from "../../../../../../store/transaction-certificates.store";

interface FarmCapacityInfoProps {
  formRef:any,
  facilityData: FacilityInformation;
  facilityFarmCapacityInfo: FacilityFarmCapacityInformation[]; // Expecting an array
  setFacilityFarmCapacityInfo: React.Dispatch<
    React.SetStateAction<FacilityFarmCapacityInformation[]>
  >;
}

const FacilityFarmCapacityInfo: React.FC<FarmCapacityInfoProps> = ({
  formRef,
  facilityFarmCapacityInfo,
  setFacilityFarmCapacityInfo
}) => {
  const { updateField } = useFormActions("sc-facilitydetails");
  const [data, setData] = useState<FacilityFarmCapacityInformation[]>(
    facilityFarmCapacityInfo
  ); // Initialize with prop data
  const [tableData, setTableData] = useState<FacilityFarmCapacityInformation[]>(
    facilityFarmCapacityInfo
  ); // Initialize with prop data
  const [editingRowIndex, setEditingRowIndex] = useState<number>(-1);
  const [rawMaterialCode,setRawMaterialCode] = useState<any[]>([]);
  const { isRestrictPopUpSave, setRestrictPopUpSave, setFacilityProcessCategoryCodes}=useTransactionCertificateStore()

  const [showWarning, setShowWarning] = useState(false);
  const [warningMessage, setWarningMessage] = useState<string | null>(null);
    
  useEffect(() => {
      setRestrictPopUpSave(false);
    return (()=>{
      setRestrictPopUpSave(false);
    })
  }, [])


  const validationSchema = Yup.object().shape({
    facilityFarmRawMaterialCode: Yup.string().required("Please enter farm raw material code"),
    facilityFarmRawMaterialCapacity: Yup.string().required("Please enter farm raw material capacity"),
  });

  useEffect(() => {
    const getRawMaterialCodes = async () => {
        try {
          const result: any = await dtrackItServiceV4.getRawMaterialCodes()
          //(`materiaCodes result ${JSON.stringify(result)}`)
  
          const materiaCodes = result.data.map((t: any) => ({ value: t.code_1, label: `${t.description_1} (${t.code_1})` }))
          //(`materiaCodes ${materiaCodes}`)
          setRawMaterialCode(materiaCodes);
        } catch (err) {
          //(`Error in Fetching Organic Farm Standard Code ${err}`)
        }
      };

    getRawMaterialCodes();
  }, [])
  useEffect(() => {
    updateField("facilityProcessDetails", data)
  }, [data])

  const formik = useFormik<FacilityFarmCapacityInformation>({
    initialValues: {
      facilityFarmRawMaterialCode: "",
      facilityFarmRawMaterialCapacity: "",
      facilityFarmRawMaterialCodeDescription:""
    },
    validationSchema,
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: () => {
      // Submission logic if needed
    },
  });

  const handleCellChange = (
    rowIndex: number,
    columnId: string,
    value: any
  ) => {
    formik.setFieldValue(columnId, value);
    setData((prevData) =>
      prevData.map((row, index) =>
        index === rowIndex ? { ...row, [columnId]: value } : row
      )
    );
  };

  const handleAddNewRow = () => {
    const newRow: FacilityFarmCapacityInformation = {
      facilityFarmRawMaterialCode: "",
      facilityFarmRawMaterialCapacity: "",
      facilityFarmRawMaterialCodeDescription:""
    };
    setRestrictPopUpSave(true);
    const newData = [...data,newRow];
    setData(newData);
    setEditingRowIndex(data.length);
    formik.setValues(newRow);
    formik.setTouched({});
  };

  const handleCancelEdit = async () => {
    setData(tableData);

    // Reset editing state and form
    setRestrictPopUpSave(false);
    setEditingRowIndex(-1);
    formik.resetForm();
  };

  const handleSaveRow = () => {
    if (editingRowIndex === null || !formik.isValid) return;

    const updatedRow = {...formik.values,
      facilityFarmRawMaterialCodeDescription:rawMaterialCode?.find((rm:any)=>rm?.value===formik?.values?.facilityFarmRawMaterialCode)?.label
    };
    // setSubmissionFlag(updatedRow)
    // const message = getValidationMessage(updatedRow);
    // setShowWarning(!!message);
    // setWarningMessage(message);

    const newData = data?.map((row, index) =>
        index === editingRowIndex ? updatedRow : row
      );
    setData(newData);
    setTableData(newData);
    setFacilityFarmCapacityInfo(newData); // Update parent with current data
    setRestrictPopUpSave(false);
    setEditingRowIndex(-1);
    formik.resetForm();
  };

  const columns: EditableColumnDef<FacilityFarmCapacityInformation>[] = [
    {
      accessorKey: "facilityFarmRawMaterialCode",
      header: "Facility Farm Raw Material Code",
      Header: <span> <i style={{ color: 'red' }}>* </i>Facility Farm Raw Material Code</span>,
      renderEditCell: ({ value, onChange }) => (
        <CustomSelect
          name="facilityFarmRawMaterialCode"
          value={value}
          placeholder="Enter value"
          options={rawMaterialCode}
          required = {true}
          enableSearch={true}
          onChange={(e) => {
            onChange(e.target.value)
          }}
        />
      ),
    },
    {
      accessorKey: "facilityFarmRawMaterialCapacity",
      header: "Facility Farm Raw Material Capacity",
      Header: <span> <i style={{ color: 'red' }}>* </i>Facility Farm Raw Material Capacity</span>,
      renderEditCell: ({ value, onChange }) => (
        <CustomTextField
          name="facilityFarmRawMaterialCapacity"
          required = {true}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Enter value"
          sx={{ mb: 2 }}
        />

      ),
    },
  ];

  const handleEditRow = (rowIndex: number) => {
    setEditingRowIndex(rowIndex);
    setRestrictPopUpSave(true);
    formik.setValues(data[rowIndex]);
  };

  const handleDeleteRow = (rowIndex: number) => {
    const updatedData = data.filter((_, index) => index !== rowIndex);
    setData(updatedData);
    setTableData(updatedData);
    setFacilityFarmCapacityInfo(updatedData); // Update parent with updated data
  };

  return (
    <div>
      <Box
        component="main"
        sx={{ flexGrow: 1, p: 1, border: "2px solid rgba(128, 128, 128, .2)", my: 4 }}
      >
        <Box display="flex" mb={3} justifyContent="flex-end" width="100%">
          <StyledButton disabled={!formik.isValid || isRestrictPopUpSave} onClick={handleAddNewRow} sx={{ mb: 2 }}>
            Add Facility Farm Capacity Information
          </StyledButton>
        </Box>
        {showWarning && (
          <Box sx={{ p: 2, mb: 2, bgcolor: '#FFF3E0', borderRadius: 1, border: '1px solid #FFB74D' }}>
            <Typography color="warning.dark">
              {warningMessage}
            </Typography>
          </Box>
        )}
        <EditableTable
          data={data}
          columns={columns}
          onSaveRow={handleSaveRow}
          onCellChange={handleCellChange}
          editingRowIndex={editingRowIndex}
          onEditRow={handleEditRow}
          onCancelEdit={handleCancelEdit}
          onDeleteRow={handleDeleteRow}
        />
      </Box>
    </div>
  );
};

export default FacilityFarmCapacityInfo;
